import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { getErrorMessage } from '@/lib/api'
import { workerName } from '@/pages/attendance/data'
import { SearchableSelect } from '@/components/common/searchable-select'
import { locationsApi } from '@/services/locations'
import type { Worker } from '@/types'

const NO_LOCATION = '__none__'

type PunchDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  workers: Worker[]
  workerId?: string
  lockWorker?: boolean
  initialStatus?: boolean
  isOnline?: boolean
  isPending?: boolean
  onSubmit: (payload: {
    workerId: string
    status: boolean
    timestamp?: string
    latitude?: number
    longitude?: number
    locationId?: string
  }) => Promise<unknown> | void
}

export function AdminAttendanceDialog({
  open,
  onOpenChange,
  workers,
  workerId = '',
  lockWorker = false,
  initialStatus = true,
  isOnline = false,
  isPending = false,
  onSubmit,
}: PunchDialogProps) {
  const [locationId, setLocationId] = useState('')
  const [selectedWorkerId, setSelectedWorkerId] = useState(workerId)
  const [status, setStatus] = useState(initialStatus)
  const [timestamp, setTimestamp] = useState('')
  const [latitude, setLatitude] = useState('')
  const [longitude, setLongitude] = useState('')

  const locationsQuery = useQuery({
    queryKey: ['attendance-live-locations'],
    queryFn: () => locationsApi.list(),
    enabled: open,
  })
  // Checkposts are sub-points inside a site, not places a worker punches in at.
  const locations = useMemo(
    () => (locationsQuery.data ?? []).filter((location) => location.type !== 'Checkpost' && location.active !== false),
    [locationsQuery.data],
  )
  const hasLocations = locations.length > 0
  const workerOptions = useMemo(
    () => workers.map((worker) => ({
      value: String(worker.worker_id ?? worker.id),
      label: workerName(worker),
      keywords: [worker.worker_code, worker.user_registration_number, worker.phone_number, worker.email]
        .filter(Boolean)
        .join(' '),
    })),
    [workers],
  )

  useEffect(() => {
    if (!open) return
    setLocationId('')
    setSelectedWorkerId(workerId)
    setStatus(initialStatus)
    setTimestamp('')
    setLatitude('')
    setLongitude('')
  }, [initialStatus, open, workerId])

  const submit = async () => {
    if (!selectedWorkerId) {
      toast.error('Select an employee')
      return
    }
    const selectedWorker = workers.find((worker) => String(worker.worker_id ?? worker.id) === selectedWorkerId)
    const alreadyOnline = Boolean(selectedWorker?.is_online ?? selectedWorker?.isOnline) || (isOnline && selectedWorkerId === workerId)
    if (status && alreadyOnline) {
      toast.error('Cannot check-in: latest worker attendance is online. Please close it first.')
      return
    }
    const lat = latitude.trim() ? Number(latitude) : undefined
    const lng = longitude.trim() ? Number(longitude) : undefined
    if (lat !== undefined && Number.isNaN(lat)) {
      toast.error('Latitude must be a valid number')
      return
    }
    if (lng !== undefined && Number.isNaN(lng)) {
      toast.error('Longitude must be a valid number')
      return
    }
    try {
      await onSubmit({
        workerId: selectedWorkerId,
        status,
        timestamp: timestamp || undefined,
        latitude: lat,
        longitude: lng,
        locationId: locationId || undefined,
      })
      onOpenChange(false)
    } catch (error) {
      toast.error(getErrorMessage(error))
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{status ? 'Check in' : 'Check out'}</DialogTitle>
          <DialogDescription>
            Uses the same attendance record as the worker profile. Leave the time empty to use the current time, or set any date and time.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="punch-location">Location <span className="text-xs text-muted-foreground">(optional)</span></Label>
            <Select
              value={locationId || NO_LOCATION}
              onValueChange={(value) => setLocationId(value === NO_LOCATION ? '' : value)}
              disabled={locationsQuery.isLoading || !hasLocations}
            >
              <SelectTrigger id="punch-location" aria-label="Location">
                <SelectValue placeholder={locationsQuery.isLoading ? 'Loading locations...' : 'No location'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_LOCATION}>No location</SelectItem>
                {locations.map((location) => (
                  <SelectItem key={String(location.id)} value={String(location.id)}>
                    {location.place}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!locationsQuery.isLoading && !hasLocations ? (
              <p className="text-xs text-muted-foreground">No locations yet. The punch will not be linked to a location.</p>
            ) : null}
          </div>
          <div className="space-y-2">
            <Label htmlFor="punch-employee">Employee</Label>
            <SearchableSelect
              id="punch-employee"
              ariaLabel="Employee"
              value={selectedWorkerId}
              onValueChange={setSelectedWorkerId}
              options={workerOptions}
              placeholder="Select employee"
              searchPlaceholder="Search by name or ID..."
              emptyText="No employees in this branch."
              disabled={lockWorker}
            />
          </div>
          <div className="space-y-2">
            <Label>Status <span className="text-xs text-muted-foreground">(optional, defaults to online)</span></Label>
            <Select value={status ? 'online' : 'offline'} onValueChange={(value) => setStatus(value === 'online')}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="online">Online</SelectItem>
                <SelectItem value="offline">Offline</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Timestamp <span className="text-xs text-muted-foreground">(optional)</span></Label>
            <Input
              type="datetime-local"
              value={timestamp}
              placeholder="Leave empty to use current time"
              onChange={(event) => setTimestamp(event.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Latitude <span className="text-xs text-muted-foreground">(optional)</span></Label>
              <Input type="number" step="0.0001" placeholder="e.g., 40.7128" value={latitude} onChange={(event) => setLatitude(event.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Longitude <span className="text-xs text-muted-foreground">(optional)</span></Label>
              <Input type="number" step="0.0001" placeholder="e.g., -74.0060" value={longitude} onChange={(event) => setLongitude(event.target.value)} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>Cancel</Button>
          <Button type="button" onClick={() => void submit()} disabled={isPending}>
            {isPending ? 'Saving...' : 'Create Attendance'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
